import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Loader2, Package, Trash2, CheckCircle2, Circle, Plus } from 'lucide-react';
import { productsApi, CreateProductDto, UpdateProductDto, packagingContainersApi, PackagingContainer, ProductPackagingDto } from '../../../services/api';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface ProductFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

const ProductSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-2"><div className="h-4 w-20 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
      <div className="space-y-2"><div className="h-4 w-20 bg-slate-100 rounded" /><div className="h-10 bg-slate-200 rounded-lg" /></div>
    </div>
    <div className="space-y-2"><div className="h-4 w-20 bg-slate-100 rounded" /><div className="h-24 bg-slate-200 rounded-lg" /></div>
    <div className="flex items-center space-x-2"><div className="h-5 w-5 bg-slate-200 rounded" /><div className="h-4 w-24 bg-slate-100 rounded" /></div>
    <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 space-y-4">
      <div className="h-4 w-32 bg-slate-200 rounded" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="h-10 bg-slate-200 rounded-lg" />
        <div className="h-10 bg-slate-200 rounded-lg" />
        <div className="h-10 bg-slate-200 rounded-lg" />
      </div>
    </div>
  </div>
);

export const ProductForm: React.FC<ProductFormProps> = ({ 
  isOpen, 
  onOpenChange, 
  productId, 
  onClose, 
  onSuccess 
}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'packaging'>('general');
  
  // Product State
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSellable, setIsSellable] = useState(true);
  const [taxRate, setTaxRate] = useState<number | null>(null);
  const [warehouseLink, setWarehouseLink] = useState<boolean>(false);
  const [adjustByKilos, setAdjustByKilos] = useState<boolean>(false);
  const [family, setFamily] = useState('');
  const [variety, setVariety] = useState('');
  const [category, setCategory] = useState('');
  const [tdto, setTdto] = useState('');
  const [printLines, setPrintLines] = useState<number>(3);
  
  // Packaging State
  const [assignedPackaging, setAssignedPackaging] = useState<ProductPackagingDto[]>([]);
  const [allContainers, setAllContainers] = useState<PackagingContainer[]>([]);
  const [defaultPackagingId, setDefaultPackagingId] = useState<string | null>(null);
  
  // New Assignment State
  const [selectedContainerId, setSelectedContainerId] = useState<string>('');
  const [weightPerBox, setWeightPerBox] = useState<number | undefined>(undefined);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('general');
      if (productId) {
        loadProduct();
      } else {
        resetForm();
      }
      loadContainers();
    }
  }, [isOpen, productId]);

  const resetForm = () => {
    setName('');
    setExternalId('');
    setDescription('');
    setIsActive(true);
    setIsSellable(true);
    setTaxRate(null);
    setWarehouseLink(false);
    setAdjustByKilos(false);
    setFamily('');
    setVariety('');
    setCategory('');
    setTdto('');
    setPrintLines(3);
    setAssignedPackaging([]);
    setDefaultPackagingId(null);
    setError(null);
  };

  const loadContainers = async () => {
    try {
      const data = await packagingContainersApi.getAll();
      setAllContainers(data);
    } catch (err) {
      console.error('Error loading containers:', err);
    }
  };

  const loadProduct = async () => {
    try {
      setIsFetching(true);
      setError(null);
      const product = await productsApi.getById(productId!);
      setName(product.name || '');
      setExternalId(product.externalId || '');
      setDescription(product.description || '');
      setIsActive(product.isActive);
      setIsSellable(product.isSellable ?? true);
      setTaxRate(product.taxRate ?? null);
      setWarehouseLink(product.warehouseLink ?? false);
      setAdjustByKilos(product.adjustByKilos ?? false);
      setFamily(product.family || '');
      setVariety(product.variety || '');
      setCategory(product.category || '');
      setTdto(product.tdto || '');
      setPrintLines(product.printLines ?? 3);
      
      // Load Assignment fields if available
      setAssignedPackaging(product.availablePackaging || []);
      setDefaultPackagingId(product.defaultPackagingContainerId || null);
    } catch (err: any) {
      console.error('Error loading product:', err);
      setError(err.message || 'Error al cargar el producto');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Por favor ingrese el nombre del producto');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data: CreateProductDto | UpdateProductDto = {
        name: name.trim(),
        externalId: externalId.trim() || null,
        description: description.trim() || null,
        isActive,
        isSellable,
        taxRate: taxRate,
        warehouseLink: warehouseLink,
        adjustByKilos: adjustByKilos,
        family: family.trim() || null,
        variety: variety.trim() || null,
        category: category.trim() || null,
        tdto: tdto.trim() || null,
        printLines: printLines,
      };

      if (productId) {
        await productsApi.update(productId, data as UpdateProductDto);
      } else {
        await productsApi.create(data as CreateProductDto);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving product:', err);
      setError(err.message || 'Error al guardar el producto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignPackaging = async () => {
    if (!productId || !selectedContainerId) return;
    
    try {
      setIsSubmitting(true);
      await packagingContainersApi.assignToProduct(selectedContainerId, productId, {
        weightPerBoxKg: weightPerBox
      });

      // If it's the first one, set as default
      if (assignedPackaging.length === 0) {
        await packagingContainersApi.setDefaultForProduct(selectedContainerId, productId);
      }

      // Reload product to refresh list
      await loadProduct();

      // Reset assignment fields
      setSelectedContainerId('');
      setWeightPerBox(undefined);
      
      alert('Envase asignado');
    } catch (err: any) {
      alert(err.message || 'Error al asignar envase');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetDefault = async (containerId: string) => {
    if (!productId) return;
    try {
      await packagingContainersApi.setDefaultForProduct(containerId, productId);
      setDefaultPackagingId(containerId);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpdateWeight = async (containerId: string, weight: number | undefined) => {
    if (!productId) return;
    try {
      await packagingContainersApi.updateProductPackaging(containerId, productId, {
        weightPerBoxKg: weight
      });
      setAssignedPackaging(prev => prev.map(ap =>
        ap.packagingContainerId === containerId
          ? { ...ap, weightPerBoxKg: weight ?? null }
          : ap
      ));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUnassignPackaging = async (containerId: string) => {
    if (!productId || !confirm('¿Desasignar este envase del producto?')) return;
    try {
      await packagingContainersApi.unassignFromProduct(containerId, productId);
      await loadProduct();
    } catch (err: any) {
      alert(err.message);
    }
  };

  // Filter containers that are NOT already assigned
  const availableToAssign = allContainers.filter(c => 
    !assignedPackaging.some(ap => ap.packagingContainerId === c.id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 flex flex-col sm:max-w-5xl max-w-5xl max-h-[90vh] min-h-[70vh]">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>{productId ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
        </DialogHeader>

        <div className="flex border-b px-6">
          <button
            type="button"
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'general' 
                ? 'border-green-600 text-green-700' 
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          {productId && (
            <button
              type="button"
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'packaging' 
                  ? 'border-green-600 text-green-700' 
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
              onClick={() => setActiveTab('packaging')}
            >
              Envases
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isFetching ? (
            <ProductSkeleton />
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 mb-6">
                  <AlertCircle className="text-red-600 h-5 w-5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-red-800 font-bold">Error</p>
                    <p className="text-red-600">{error}</p>
                  </div>
                </div>
              )}

              {activeTab === 'general' ? (
                <form id="product-form" onSubmit={handleSubmit} className="space-y-6">
                  {/* Form fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="product-name" className="text-green-800 font-bold">Nombre del Producto *</Label>
                      <Input
                        id="product-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ej. Lechuga Iceberg"
                        required
                        className="border-green-200 focus-visible:ring-green-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ext-id" className="text-green-800 font-bold">Código Externo</Label>
                      <Input
                        id="ext-id"
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        placeholder="Ej. PROD-001"
                        className="border-green-200 focus-visible:ring-green-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="desc" className="text-green-800 font-bold">Descripción</Label>
                    <Textarea
                      id="desc"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Ej. Lechuga fresca..."
                      className="border-green-200 focus-visible:ring-green-500 min-h-[100px]"
                    />
                  </div>

                  <div className="flex flex-wrap gap-6">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="isActiveProd" checked={isActive} onCheckedChange={(checked) => setIsActive(!!checked)} />
                      <Label htmlFor="isActiveProd" className="cursor-pointer">Producto activo</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="isSellableProd" checked={isSellable} onCheckedChange={(checked) => setIsSellable(!!checked)} />
                      <Label htmlFor="isSellableProd" className="cursor-pointer">Disponible para venta</Label>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Información Técnica</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tax">IVA (%)</Label>
                        <Input id="tax" type="number" step="0.01" value={taxRate ?? ''} onChange={(e) => setTaxRate(e.target.value ? parseFloat(e.target.value) : null)} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lines">Líneas Impresión</Label>
                        <Input id="lines" type="number" value={printLines} onChange={(e) => setPrintLines(parseInt(e.target.value) || 3)} />
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">Asignar Envase</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Tipo de Envase</Label>
                        <Select value={selectedContainerId} onValueChange={setSelectedContainerId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar envase..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableToAssign.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.code}) - {c.lengthCm}x{c.widthCm}cm
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Peso por caja (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={weightPerBox ?? ''}
                          onChange={(e) => setWeightPerBox(e.target.value ? Number(e.target.value) : undefined)}
                          placeholder="Peso producto por caja"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <Button 
                        size="sm" 
                        onClick={handleAssignPackaging}
                        disabled={!selectedContainerId || isSubmitting}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Asignar
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-medium">Envases Asignados</h3>
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                      El peso indicado debe ser solo del producto, sin incluir el peso de la caja. La tara del envase se suma automáticamente.
                    </p>
                    {assignedPackaging.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed">
                        No hay envases asignados a este producto.
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {[...assignedPackaging].sort((a, b) => {
                          const aDefault = a.packagingContainerId === defaultPackagingId ? 0 : 1;
                          const bDefault = b.packagingContainerId === defaultPackagingId ? 0 : 1;
                          return aDefault - bDefault;
                        }).map(ap => {
                          const isDefault = ap.packagingContainerId === defaultPackagingId;
                          return (
                            <div key={ap.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDefault ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-slate-100 rounded-full text-slate-600">
                                  <Package className="h-4 w-4" />
                                </div>
                                <div>
                                  <div className="font-medium text-slate-900 flex items-center gap-2">
                                    {ap.packagingContainerName}
                                    {isDefault && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Predeterminado</span>}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {ap.packagingContainerCode}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="relative w-28">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    placeholder="—"
                                    className="h-8 text-sm pr-12"
                                    defaultValue={ap.weightPerBoxKg ?? ''}
                                    onBlur={(e) => {
                                      const val = e.target.value ? Number(e.target.value) : undefined;
                                      if (val !== (ap.weightPerBoxKg ?? undefined)) {
                                        handleUpdateWeight(ap.packagingContainerId, val);
                                      }
                                    }}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">kg</span>
                                </div>
                                {!isDefault && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleSetDefault(ap.packagingContainerId)}
                                    title="Marcar como predeterminado"
                                  >
                                    <CheckCircle2 className="h-4 w-4 text-slate-400 hover:text-green-600" />
                                  </Button>
                                )}
                                {isDefault && (
                                  <Button size="sm" variant="ghost" disabled>
                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleUnassignPackaging(ap.packagingContainerId)}
                                  title="Desasignar envase"
                                >
                                  <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="p-6 border-t bg-slate-50/30">
          <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
            {activeTab === 'packaging' ? 'Cerrar' : 'Cancelar'}
          </Button>
          {activeTab === 'general' && (
            <Button type="submit" form="product-form" disabled={isSubmitting || isFetching} className="bg-green-600 hover:bg-green-700 min-w-[140px]">
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {isSubmitting ? 'Guardando...' : productId ? 'Actualizar' : 'Guardar Producto'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
